import React, { useCallback, useMemo, useState } from 'react';
import { Button, Form, Modal, ModalProps } from 'react-bootstrap';
import { Auth, CognitoUser } from '@aws-amplify/auth';

import config from '@/lib/config';
import { usePlatformsContext } from '@/hooks';
import { LoadingButton } from '@/components/loading-button';
import { OtpInput } from '@/components/otp-input';
import { ACTION_STATUS, ACTION_TYPES, CHALLENGE_NAMES } from '@/lib/models';
import { trackAction, toErrorFields } from '@/lib/utils';
import RememberDeviceField from './remember-device-field';
import { ReactComponent as ContactUsIcon } from '@/assets/icons/contact-us.svg';
import { ReactComponent as SendIcon } from '@/assets/icons/send-icon.svg';
import { ReactComponent as ExclamationIcon } from '@/assets/icons/exclamation-icon.svg';

interface MfaCodeEntryModalProps extends ModalProps {
	user: CognitoUser;
	onSuccess(user: CognitoUser): void;
	onResendCode?(): Promise<void> | void;
}

const DialogAs = (dialogProps: any) => (
	<Modal.Dialog
		{...dialogProps}
		style={{ maxWidth: 'none', width: 'fit-content', marginLeft: 'auto', marginRight: 'auto' }}
	/>
);

export const MfaCodeEntryModal = ({ user, onSuccess, onResendCode, ...props }: MfaCodeEntryModalProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [codeInputValue, setCodeInputValue] = useState('');
	const [codeMessage, setCodeMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
	const { platforms } = usePlatformsContext();
	const platform = useMemo(() => platforms[0], [platforms]);

	const handleOnEnter = useCallback(() => {
		setIsLoading(false);
		setCodeMessage(null);
	}, []);

	async function submitCode(rawCode: string) {
		setIsLoading(true);
		setCodeMessage(null);

		try {
			let confirmedUser: CognitoUser;

			if (config.HACKETT_USER_AUTH_USE_REAL_AUTH === 'true') {
				confirmedUser = await Auth.confirmSignIn(
					user,
					rawCode.replace(/\s/g, ''),
					(user.challengeName as unknown) as undefined
				);
			} else {
				confirmedUser = user;
			}

			onSuccess(confirmedUser);
		} catch (error) {
			trackAction({
				actionType: ACTION_TYPES.ACCOUNT_SIGNED_IN,
				status: ACTION_STATUS.FAILURE,
				emailAddress: user.getUsername?.(),
				...toErrorFields(error),
			});

			const message =
				(error as any).code === 'CodeMismatchException' || (error as any).name === 'CodeMismatchException'
					? 'The code is invalid please try again or send a new code'
					: (error as Error).message || 'Unable to confirm code.';

			setCodeMessage({ kind: 'error', text: message });
		} finally {
			setIsLoading(false);
		}
	}

	function handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		submitCode(codeInputValue);
	}

	async function handleResendCode() {
		try {
			setIsLoading(true);
			setCodeMessage(null);
			if (onResendCode) {
				await onResendCode();
			}
			trackAction({
				actionType: ACTION_TYPES.MFA_REQUEST_SMS_NEW_CODE,
				status: ACTION_STATUS.SUCCESS,
				emailAddress: user.getUsername?.(),
			});
			setCodeInputValue('');
			setCodeMessage({ kind: 'success', text: 'A new verification code has been sent via SMS.' });
		} catch (error) {
			trackAction({
				actionType: ACTION_TYPES.MFA_REQUEST_SMS_NEW_CODE,
				status: ACTION_STATUS.FAILURE,
				emailAddress: user.getUsername?.(),
				...toErrorFields(error),
			});
			setCodeMessage({ kind: 'error', text: (error as Error).message || 'Unable to send a new code.' });
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Modal centered onEnter={handleOnEnter} dialogAs={DialogAs} {...props}>
			<Modal.Header>
				<Modal.Title>Multi-Factor Authentication</Modal.Title>
			</Modal.Header>
			<Form onSubmit={handleFormSubmit}>
				<Modal.Body style={{ paddingTop: 20, paddingBottom: 20 }}>
					{user.challengeName === CHALLENGE_NAMES.SOFTWARE_TOKEN_MFA && (
						<p className="mb-3">
							Open your authentication app (TOTP) on your mobile device <br /> to view your authentication
							code.
						</p>
					)}
					{user.challengeName === CHALLENGE_NAMES.SMS_MFA && (
						<p className="mb-3">
							We have delivered the authentication code via SMS. <br />
							Please enter the code to complete authentication.
						</p>
					)}
					<Form.Group className="mb-0">
						<Form.Label>Authentication Code</Form.Label>
						<div className="d-flex align-items-center" style={{ gap: 24 }}>
							<OtpInput
								value={codeInputValue}
								onChange={(next) => {
									setCodeInputValue(next);
									if (codeMessage) setCodeMessage(null);
								}}
								onComplete={(code) => submitCode(code)}
								autoFocus
								name="mfa-code"
								hasError={codeMessage?.kind === 'error'}
							/>
							{user.challengeName === CHALLENGE_NAMES.SMS_MFA && (
								<Button
									variant="link"
									onClick={handleResendCode}
									className="d-flex align-items-center p-0"
									style={{ gap: 8 }}
									disabled={isLoading}
								>
									<SendIcon />
									Send a New Code
								</Button>
							)}
						</div>
					</Form.Group>
					{config.HACKETT_USER_AUTH_REMEMBER_DEVICE_ENABLED && <RememberDeviceField />}
					{codeMessage && (
						<div
							className={`d-flex align-items-center mt-2 mb-0 ${
								codeMessage.kind === 'error' ? 'text-danger' : ''
							}`}
							style={{ gap: 8, fontSize: 14 }}
						>
							{codeMessage.kind === 'error' ? <ExclamationIcon /> : <SendIcon />}
							<span>{codeMessage.text}</span>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex align-items-center w-100" style={{ gap: 8 }}>
						<a
							href={`mailto:${platform?.supportEmailAddress || 'zarasa@hackett.com'}`}
							className="d-inline-flex align-items-center text-info text-nowrap mr-auto"
							style={{ padding: '1.2rem 2.4rem', gap: 8 }}
						>
							<ContactUsIcon className="flex-shrink-0" />
							Contact Us
						</a>
						<Button
							variant="outline-primary"
							onClick={props.onHide}
							className="d-flex justify-content-center align-items-center text-nowrap"
							style={{ flex: '1 1 0', minWidth: 0 }}
							disabled={isLoading}
						>
							Cancel
						</Button>
						<LoadingButton
							isLoading={isLoading}
							disabled={!codeInputValue}
							variant="primary"
							className="d-flex justify-content-center align-items-center text-nowrap"
							style={{ flex: '1 1 0', minWidth: 0 }}
						>
							Submit
						</LoadingButton>
					</div>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};
